/**
 * Census of unsaved widget config, including queued and in-flight writes.
 * The shared write buffer registers for its lifetime so route changes don't
 * hide saves that are still running after their widgets unmount.
 *
 * Lives here rather than in the hook so the recovery signal collector doesn't
 * have to reach into features/, and so it can be tested without React.
 *
 * `flush()` uses mutateAsync + Promise.allSettled under the hood (see
 * use-widget-config-sync.ts) and restores an entry to pending if its write
 * fails, so a failed save is retried on the next flush rather than silently
 * lost. Used by Level 3 to make sure widget layout/config edits are durable
 * before a reload discards the in-memory copy.
 */

export type PendingWidgetConfigSource = {
  hasPending: () => boolean
  /** Flushes this instance's pending writes. Never rejects — failures are
   *  restored to pending and reported via console, not thrown. */
  flush: () => Promise<void>
}

const sources = new Set<PendingWidgetConfigSource>()
const listeners = new Set<() => void>()

export function notifyPendingWidgetConfigChange(): void {
  listeners.forEach((listener) => listener())
}

export function subscribePendingWidgetConfig(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Registers a save source. Returns an unregister function. */
export function registerPendingWidgetConfigSource(source: PendingWidgetConfigSource): () => void {
  sources.add(source)
  notifyPendingWidgetConfigChange()
  return () => {
    sources.delete(source)
    notifyPendingWidgetConfigChange()
  }
}

/** True when any source still holds queued or in-flight widget config. */
export function hasPendingWidgetConfig(): boolean {
  for (const source of sources) {
    if (source.hasPending()) return true
  }
  return false
}

/**
 * Flushes every live instance's pending config. Best-effort: a failed write
 * stays pending (see PendingWidgetConfigSource.flush) rather than being lost,
 * so callers should re-check hasPendingWidgetConfig() after this resolves
 * instead of assuming success.
 */
export function flushAllPendingWidgetConfigs(): Promise<void> {
  return Promise.allSettled(Array.from(sources).map((source) => source.flush())).then(() => {})
}

/** Test seam. */
export function resetPendingWidgetConfigSourcesForTest(): void {
  sources.clear()
}
